import type { CashSessionRow } from '../infra/cashSessionsRepo';
import type { CashSummary } from '../infra/cashSummaryQuery';
import type { BranchAccess } from '../../shell/session/store/sessionTypes';
import type { EmployeeRecord } from '../../team/store/useEmployeesStore';
import { resolveCashOperatorLabel } from './cashOperatorLabels';
import type { CashHistoryRangePreset } from './cashList.constants';
export type { CashHistoryRangePreset };

/** Rango de consulta para SQLite según preset y fechas personalizadas. */
export function computeCashHistoryQueryRange(
  preset: CashHistoryRangePreset,
  customFrom: Date,
  customTo: Date
): { fromIso: string; toIso: string } {
  if (preset === 'custom') {
    const start = new Date(customFrom);
    start.setHours(0, 0, 0, 0);
    const end = new Date(customTo);
    end.setHours(23, 59, 59, 999);
    if (start.getTime() > end.getTime()) {
      return { fromIso: end.toISOString(), toIso: start.toISOString() };
    }
    return { fromIso: start.toISOString(), toIso: end.toISOString() };
  }
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const from = new Date();
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);
  return { fromIso: from.toISOString(), toIso: to.toISOString() };
}

/** Duración legible del turno abierto (solo presentación). */
export function formatTurnOpenDuration(openedAt: string, nowMs = Date.now()): string {
  const ms = Math.max(0, nowMs - new Date(openedAt).getTime());
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return 'Recién abierta';
  if (totalMin < 60) return `Abierta hace ${totalMin} min`;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours < 24) {
    return mins > 0 ? `Abierta hace ${hours} h ${mins} min` : `Abierta hace ${hours} h`;
  }
  const days = Math.floor(hours / 24);
  return `Abierta hace ${days} ${days === 1 ? 'día' : 'días'}`;
}

/** Duración compacta para tarjetas KPI (label aparte explica el contexto). */
export function formatTurnOpenDurationCompact(openedAt: string, nowMs = Date.now()): string {
  const ms = Math.max(0, nowMs - new Date(openedAt).getTime());
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return '<1m';
  if (totalMin < 60) return `${totalMin}m`;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

function sessionOpenedAtMs(session: CashSessionRow | null | undefined): number {
  if (!session?.openedAt) return 0;
  const ms = new Date(session.openedAt).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function pickActiveSession(remote: CashSummary, local: CashSummary): CashSessionRow | null {
  const remoteSession = remote.activeSession;
  const localSession = local.activeSession;
  // Remoto open manda: un cierre local no confirmado en el server no debe ocultar el turno
  // (si el close quedó pendiente, reconcileCashSessionState re-empuja el cierre).
  // Antes: local.lastClosed === remote.active → null → UI “Abrir” + spam already_open.
  if (localSession && !remoteSession) return localSession;
  if (remoteSession && !localSession) return remoteSession;
  if (!localSession && !remoteSession) return null;
  if (localSession!.id === remoteSession!.id) return localSession;
  return sessionOpenedAtMs(localSession) >= sessionOpenedAtMs(remoteSession)
    ? localSession!
    : remoteSession!;
}

/**
 * Fusiona resumen remoto (colaborativo) con local (operación del dispositivo).
 */
const SESSION_SALES_COUNT_WEIGHT = 1_000_000;

function sessionMetricsScore(summary: CashSummary): number {
  return (
    (summary.sessionSalesCount ?? 0) * SESSION_SALES_COUNT_WEIGHT +
    (summary.sessionSalesTotal ?? 0) +
    (summary.sessionCashTotal ?? 0)
  );
}

function pickSessionMetrics(
  remote: CashSummary,
  local: CashSummary,
  activeSessionId: string
): Pick<
  CashSummary,
  | 'sessionCashTotal'
  | 'sessionSalesCount'
  | 'sessionSalesTotal'
  | 'sessionTipsTotal'
  | 'sessionPaymentBreakdown'
  | 'sessionOutflowsTotal'
  | 'sessionInflowsTotal'
> {
  const localMatch = local.activeSession?.id === activeSessionId;
  const remoteMatch = remote.activeSession?.id === activeSessionId;
  if (!localMatch && !remoteMatch) {
    return {
      sessionCashTotal: 0,
      sessionSalesCount: 0,
      sessionSalesTotal: 0,
      sessionTipsTotal: 0,
      sessionPaymentBreakdown: [],
      sessionOutflowsTotal: 0,
      sessionInflowsTotal: 0,
    };
  }
  const source =
    localMatch && remoteMatch
      ? sessionMetricsScore(local) >= sessionMetricsScore(remote)
        ? local
        : remote
      : localMatch
        ? local
        : remote;
  return {
    sessionCashTotal: source.sessionCashTotal,
    sessionSalesCount: source.sessionSalesCount,
    sessionSalesTotal: source.sessionSalesTotal,
    sessionTipsTotal: source.sessionTipsTotal ?? 0,
    sessionPaymentBreakdown: source.sessionPaymentBreakdown,
    sessionOutflowsTotal:
      localMatch && remoteMatch
        ? Math.max(local.sessionOutflowsTotal ?? 0, remote.sessionOutflowsTotal ?? 0)
        : source.sessionOutflowsTotal,
    sessionInflowsTotal:
      localMatch && remoteMatch
        ? Math.max(local.sessionInflowsTotal ?? 0, remote.sessionInflowsTotal ?? 0)
        : source.sessionInflowsTotal,
  };
}

export function mergeCashSummaries(remote: CashSummary, local: CashSummary): CashSummary {
  const activeSession = pickActiveSession(remote, local);
  const sessionMetrics = activeSession
    ? pickSessionMetrics(remote, local, activeSession.id)
    : {
        sessionCashTotal: 0,
        sessionSalesCount: 0,
        sessionSalesTotal: 0,
        sessionTipsTotal: 0,
        sessionPaymentBreakdown: [] as CashSummary['sessionPaymentBreakdown'],
        sessionOutflowsTotal: 0,
        sessionInflowsTotal: 0,
      };

  return {
    activeSession,
    todaySalesCount: remote.todaySalesCount,
    todaySalesTotal: remote.todaySalesTotal,
    todayCashTotal: remote.todayCashTotal,
    todayDigitalTotal: remote.todayDigitalTotal,
    ...sessionMetrics,
    lastClosedSession: remote.lastClosedSession ?? local.lastClosedSession,
    // Server-authoritative cuando el remoto lo trae; fallback al local (offline).
    expectedBalance: remote.expectedBalance ?? local.expectedBalance ?? null,
  };
}

export function deriveCashBranchName(
  currentBranchId: string | null | undefined,
  branches: BranchAccess[]
): string | null {
  if (!currentBranchId) return null;
  return branches.find((b) => b.id === currentBranchId)?.name ?? null;
}

export function deriveCashOutsideSession(cashSummary: CashSummary | null): number | null {
  if (cashSummary?.activeSession == null) return null;
  // sessionCashTotal incluye ingresos manuales (direction='in'); los restamos
  // porque "fuera del turno" compara solo efectivo de ventas de hoy.
  const sessionSalesCash =
    (cashSummary.sessionCashTotal ?? 0) - (cashSummary.sessionInflowsTotal ?? 0);
  return Math.max(0, (cashSummary.todayCashTotal ?? 0) - sessionSalesCash);
}

export function deriveLastClosedOutsideHistoryRange(
  lastClosedAt: string | null | undefined,
  historyQueryRange: { fromIso: string; toIso: string }
): boolean {
  if (!lastClosedAt) return false;
  const t = new Date(lastClosedAt).getTime();
  if (!Number.isFinite(t)) return false;
  const from = new Date(historyQueryRange.fromIso).getTime();
  const to = new Date(historyQueryRange.toIso).getTime();
  return t < from || t > to;
}

export function deriveCashSessionLabels(
  cashSummary: CashSummary | null,
  employees: EmployeeRecord[],
  ownerAuthUserId: string | null | undefined,
  ownerEmail: string | null | undefined
): {
  activeSessionOpenerLabel: string | null;
  lastClosedByLabel: string | null;
  turnOpenDurationLabel: string | null;
} {
  const activeSessionOpenerLabel = resolveCashOperatorLabel(
    cashSummary?.activeSession?.openedBy,
    employees,
    ownerAuthUserId,
    ownerEmail
  );
  const lastClosedByLabel = resolveCashOperatorLabel(
    cashSummary?.lastClosedSession?.closedBy,
    employees,
    ownerAuthUserId,
    ownerEmail
  );
  const openedAt = cashSummary?.activeSession?.openedAt;
  const turnOpenDurationLabel = openedAt ? formatTurnOpenDuration(openedAt) : null;
  return { activeSessionOpenerLabel, lastClosedByLabel, turnOpenDurationLabel };
}
